// Token-1 modal bundle (generate → render → done/failed), plus session-expired.
//
// Three things changed here, all of them things a paying user would have noticed:
//
// 1. i18n. Every string used to be a Polish literal, so an EN user hit Polish at
//    the three worst moments: paying, running out of gems, and a failed render.
//    Copy now lives in locales/{pl,en}/dialogs.ts.
// 2. The dialog shell is the DS `Dialog` — focus trap, Escape, focus restore,
//    scroll lock, aria-modal. The old local `Overlay` had none of that: you could
//    Tab straight out of an open dialog into the page it was blocking.
// 3. Gem amounts are PROPS, not literals. A paid retry always names its current
//    cost before the action; render outcomes do not repeat refund copy.
//
// Token-2 PublishSheet is deliberately not here — it lives with the composer.

import type { ReactNode } from 'react'
import { useEffect, useRef } from 'react'
import Icon from '../Icon'
import { IS_NATIVE } from '../../lib/native'
import { useShellSignalsOptional } from '../../hooks/useShellSignals'
import { Dialog, usePhoneSheet } from '../../ds/feedback/Dialog'
import { useI18n } from '../../lib/i18n'
// Paczka P1 C (reguła 48): kwoty w tabelach tych okien są DANYMI, więc słowa już nie
// niosą — składa je `TokenAmount`, jeden komponent na produkt, i on oddaje pełną kwotę
// czytnikowi ekranu. `gemWord` wyleciało razem z ostatnim własnym zapisem waluty w tym
// pliku; `editWord` zostaje, bo mnożnik „2 edity × …” nazywa RZECZ, a nie walutę.
import { editWord, pluralForm } from '../../locales/plural'
import { TokenAmount } from '../../ds/primitives/TokenAmount'
import { Button } from '../../ds/primitives/Button'
import { CostTable } from '../../ds/feedback/CostTable'
import { PayLabel } from '../../ds/primitives/payLabel'

/**
 * @deprecated Import `Dialog` from `ds/feedback/Dialog` instead. Kept so existing
 * call sites (Generator, EditDetail, …) keep compiling during the migration.
 */
export const Overlay = Dialog

/** Shared summary row. `total` styles the last, emphasised row. */
function Row({ k, v, total, tone }: { k: string; v: ReactNode; total?: boolean; tone?: string }) {
  return (
    <div className={`dg-srow${total ? ' total' : ''}`}>
      <span className="k">{k}</span>
      <span className="v" style={tone ? { color: tone } : undefined}>{v}</span>
    </div>
  )
}

/* 1. Confirm a generation (Generator: count editable; re-render: count = 1). */
export function ConfirmGenerate({
  count, balance, cost, onClose, onConfirm,
}: {
  count: number
  balance: number
  /** Total gems this batch costs. Defaults to `count` (legacy 1 gem per edit). */
  cost?: number
  onClose: () => void
  onConfirm: () => void
}) {
  const { t, locale } = useI18n()
  const phone = usePhoneSheet()
  const spend = cost ?? count
  const after = balance - spend
  return (
    <Dialog
      onClose={onClose}
      title={t('dialogs.confirmGenerate.title', { count, edits: editWord(locale, count) })}
      footer={(
        <>
          {phone ? null : <button className="btn btn-ghost" onClick={onClose}>{t('dialogs.confirmGenerate.cancel')}</button>}
          <button className="btn btn-grad" onClick={onConfirm}>
            <Icon name="Sparkles" size={16} /> {t('dialogs.confirmGenerate.confirm')}
          </button>
        </>
      )}
    >
      <p className="dg-sub">{t('dialogs.confirmGenerate.body')}</p>
      <div className="dg-summary">
        <Row k={t('dialogs.confirmGenerate.afterLabel')} v={<TokenAmount locale={locale} n={after} place="table" />} />
        <Row k={t('dialogs.confirmGenerate.costLabel')} v={<TokenAmount locale={locale} n={spend} place="table" />} total />
      </div>
    </Dialog>
  )
}

/* 2. Out of gems (a 402, or pre-emptively when the balance is short).
 *
 * Język minimal, artboard 08c: tytuł „Za mało gemów”, JEDNO zdanie z trzema liczbami
 * („Masz 3, potrzeba 6. Brakuje 3.”) i jedna akcja główna — „Doładuj gemy”. Tabela
 * Potrzeba / Masz / Brakuje zniknęła, bo powtarzała co do liczby to samo zdanie; trzy
 * wiersze i zdanie o tych samych trzech liczbach to nie jest więcej informacji.
 */
export function NoTokens({
  count, balance, cost, onClose, onTopUp,
}: {
  count: number
  balance: number
  /** Total gems needed. Defaults to `count` (legacy 1 gem per edit). */
  cost?: number
  onClose: () => void
  onTopUp: () => void
}) {
  const { t, locale } = useI18n()
  const phone = usePhoneSheet()
  const need = cost ?? count
  // Ta sama flaga co „Doładuj” w panelu i „Dokup slot” (VB-7, FB-12): przy wstrzymanej
  // sprzedaży przycisk stoi szary i nigdzie nie prowadzi, bez zdania powodu (PROD2 F2-2).
  const canTopUp = useShellSignalsOptional()?.gems.canTopUp ?? true
  return (
    <Dialog
      onClose={onClose}
      title={<span data-testid="no-gems-dialog">{t('dialogs.noGems.title')}</span>}
      className="mn-dlg"
      footer={IS_NATIVE ? undefined : (
        <>
          {phone ? null : <Button variant="quiet" onClick={onClose}>{t('dialogs.noGems.back')}</Button>}
          <Button variant="primary" onClick={canTopUp ? onTopUp : undefined} disabled={!canTopUp} data-autofocus>
            {t('dialogs.noGems.topUp')}
          </Button>
        </>
      )}
    >
      {/* R5, artboard 08c: TABELA dwóch liczb zamiast zdania „Masz 0, potrzeba 3.
          Brakuje 3.” — trzecia liczba była odejmowaniem pierwszych dwóch, a zdanie
          mówiło to, co tabela pokazuje (zasada 8). */}
      <CostTable
        data-testid="no-gems-cost"
        rows={[
          { k: t('dialogs.reRender.costLabel'), v: <TokenAmount locale={locale} n={need} place="table" /> },
          { k: t('dialogs.reRender.haveLabel'), v: <TokenAmount locale={locale} n={balance} place="table" /> },
        ]}
      />
      {/* Native builds are consumption-only: no top-up CTA and no purchase prompt
          (Play/Apple billing policy). The user can only shrink the batch — which is
          the one place this dialog carries a sentence, because without the top-up
          action the table alone would leave a dead end. */}
      {IS_NATIVE ? <p className="mn-dlg-text">{t('dialogs.noGems.hintNative')}</p> : null}
    </Dialog>
  )
}

/*
 * 3. Render progress — driven by REAL polling. The parent passes the live
 * `progress` (0..100) and `status`; this fires onComplete/onFail once the job
 * reaches a terminal state.
 */
export function RenderProgress({
  progress, status, onComplete, onFail, onClose,
}: {
  progress: number
  status: 'queued' | 'rendering' | 'done' | 'failed'
  onComplete: () => void
  onFail: () => void
  onClose: () => void
}) {
  const { t } = useI18n()

  // Fire the terminal callback EXACTLY once. Parents pass fresh inline lambdas on
  // every render, so depending on onComplete/onFail would re-run the effect and
  // double-fire. A mount-scoped ref is safe: parents unmount this between flows.
  const fired = useRef(false)
  useEffect(() => {
    if (fired.current) return
    if (status === 'done') { fired.current = true; onComplete() }
    else if (status === 'failed') { fired.current = true; onFail() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]) // intentionally omit onComplete/onFail (see comment)

  const pct = Math.max(0, Math.min(100, progress))
  return (
    <Dialog wide onClose={onClose} title={t('dialogs.progress.title')}>
      <div className="render-prog">
        <span className="num mono">{pct}%</span>
        <div
          className="bar"
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={t('dialogs.progress.title')}
        >
          <i style={{ width: pct + '%' }} />
        </div>
      </div>
    </Dialog>
  )
}

/* 4. Render done. */
export function RenderDone({
  onClose, onLibrary, onDownload,
}: {
  onClose: () => void
  /** „Zobacz w Editach" — the series page when known, else the Edits list. Never „biblioteka". */
  onLibrary: () => void
  onDownload?: () => void
}) {
  const { t } = useI18n()
  return (
    <Dialog
      onClose={onClose}
      title={t('dialogs.done.title')}
      footerClassName={onDownload ? 'dg-foot--pair' : undefined}
      footer={(
        <>
          {/* „Pobierz” nie jest drogą wyjścia, tylko drugą DROGĄ do rzeczy — zostaje
              na dole także na telefonie (reguła 49 zdejmuje „Zamknij” i „Anuluj”). */}
          {onDownload && (
            <button className="btn btn-ghost" onClick={onDownload}>
              <Icon name="Download" size={16} /> {t('dialogs.done.download')}
            </button>
          )}
          <button className="btn btn-grad" onClick={onLibrary}>
            <Icon name="LayoutGrid" size={16} /> {t('dialogs.done.library')}
          </button>
        </>
      )}
    >
      <span className="sr-only">{t('dialogs.done.title')}</span>
    </Dialog>
  )
}

/* 5. Render failed — the state is the title; a paid retry names its cost before action. */
export function RenderFailed({
  onClose, onRetry, balance, cost, refunded, onTopUp,
}: {
  onClose: () => void
  onRetry: () => void
  balance?: number
  /** Cost of the next attempt. */
  cost?: number
  /** @deprecated Legacy callers passed the previous refund, equal to their retry cost. */
  refunded?: number
  onTopUp?: () => void
}) {
  const { t, locale } = useI18n()
  const phone = usePhoneSheet()
  const spend = cost ?? refunded
  if (balance != null && spend != null && balance < spend && onTopUp) {
    return <NoTokens count={1} balance={balance} cost={spend} onClose={onClose} onTopUp={onTopUp} />
  }
  return (
    <Dialog
      onClose={onClose}
      title={t('dialogs.failed.title')}
      footer={(
        <>
          {phone ? null : <Button variant="quiet" onClick={onClose}>{t('dialogs.failed.close')}</Button>}
          <Button variant="primary" onClick={onRetry}>
            <Icon name="RefreshCw" size={16} /> {spend == null
              ? t('dialogs.failed.retry')
              : <PayLabel locale={locale} verb={t('dialogs.reRender.confirm')} cost={spend} />}
          </Button>
        </>
      )}
    >
      {balance != null && spend != null && (
        <CostTable
          data-testid="failed-retry-cost"
          rows={[
            {
              k: t('dialogs.reRender.costLabel'),
              v: <>1 {editWord(locale, 1)} × <TokenAmount locale={locale} n={spend} place="table" /> = <TokenAmount locale={locale} n={spend} place="table" /></>,
            },
            { k: t('dialogs.reRender.haveLabel'), v: <TokenAmount locale={locale} n={balance} place="table" /> },
            { k: t('dialogs.reRender.leftLabel'), v: <TokenAmount locale={locale} n={balance - spend} place="table" /> },
          ]}
        />
      )}
    </Dialog>
  )
}

/* 6. Session expired — an authenticated request came back 401 mid-session. */
export function SessionExpired({ onLogin }: { onLogin: () => void }) {
  const { t } = useI18n()
  return (
    // Deliberately not dismissible: there is nothing behind it the user can act on.
    <Dialog
      title={t('dialogs.sessionExpired.title')}
      footer={(
        <button className="btn btn-grad" onClick={onLogin} data-autofocus>
          <Icon name="LogIn" size={16} /> {t('dialogs.sessionExpired.login')}
        </button>
      )}
    >
      <p className="dg-sub">{t('dialogs.sessionExpired.body')}</p>
    </Dialog>
  )
}

/* 7. Re-render confirm.
 *
 * Język minimal, artboard 08b: „Ponowić {n} edity?”, jedno zdanie o tym, CO POWSTANIE
 * (nie o zwrocie — patrz niżej) i TABELA LICZB Koszt / Masz / Zostanie. Koszt jest
 * rozpisany działaniem („2 edity × 3 gemy = 6 gemów”), bo to jedyny wiersz, w którym
 * klient może sprawdzić, skąd wzięła się suma.
 *
 * Etykieta akcji to `payLabel` — ta sama, którą niesie link na karcie serii, słowo
 * w słowo: cena przeczytana przed kliknięciem i cena na przycisku muszą się zgadzać.
 *
 * SALDO PONIŻEJ KOSZTU NIE JEST TU PYTANIEM. Wiersz „Zostanie” liczył `balance - spend`
 * bez progu, więc konto z 1 gemem przy cenie 3 czytało „Zostanie: -2 gemy” — liczbę
 * z poprawną odmianą, wyglądającą na fakt — i dowiadywało się prawdy dopiero z 402 po
 * potwierdzeniu. Reguła 19 chce tu stanu zablokowanego z powodem i drogą wyjścia,
 * więc okno oddaje głos `NoTokens` („Za mało gemów”, trzy liczby, „Doładuj gemy”).
 * Bramka u wołającego (Batches.tsx) zostaje słuszna — ta jest ostatnia, nie jedyna.
 */
export function ConfirmReRender({
  balance, cost, count = 1, onClose, onConfirm, onTopUp,
}: {
  /**
   * @deprecated Nazwa rzeczy nie stoi już w oknie (artboard 08b): okno ponowienia
   * otwiera się z ekranu, który tę rzecz właśnie pokazuje. Prop zostaje, żeby
   * `EditDetail` i Generator kompilowały się bez zmian.
   */
  title?: string
  balance: number
  /** Gems a re-render costs — za JEDEN edit. Defaults to 1 (legacy). */
  cost?: number
  /** Ile editów ponawiamy. Karta serii ponawia wszystkie nieudane naraz. */
  count?: number
  onClose: () => void
  onConfirm: () => void
  /**
   * Droga do gemów ze stanu zablokowanego. Bez niej okno idzie na `/billing` twardym
   * przejściem: wołający, który ma `navigate`, powinien go podać — ale brak propa nie
   * może zostawić klienta w ślepym zaułku.
   */
  onTopUp?: () => void
}) {
  const { t, locale } = useI18n()
  const phone = usePhoneSheet()
  const unit = cost ?? 1
  const spend = unit * count
  const after = balance - spend
  if (after < 0) {
    return (
      <NoTokens
        count={count}
        balance={balance}
        cost={spend}
        onClose={onClose}
        onTopUp={onTopUp ?? (() => { window.location.assign('/billing') })}
      />
    )
  }
  const title = t(`dialogs.reRender.title_${pluralForm(locale, count)}`, { n: count })
  return (
    <Dialog
      onClose={onClose}
      title={title}
      className="mn-dlg"
      footer={(
        <>
          {phone ? null : <Button variant="quiet" onClick={onClose}>{t('dialogs.reRender.cancel')}</Button>}
          <Button variant="primary" onClick={onConfirm}>
            <PayLabel locale={locale} verb={t('dialogs.reRender.confirm')} cost={spend} />
          </Button>
        </>
      )}
    >
      <p className="mn-dlg-text">{t('dialogs.reRender.body')}</p>
      <CostTable
        data-testid="rerender-cost"
        rows={[
          {
            k: t('dialogs.reRender.costLabel'),
            v: <>{count} {editWord(locale, count)} × <TokenAmount locale={locale} n={unit} place="table" /> = <TokenAmount locale={locale} n={spend} place="table" /></>,
          },
          { k: t('dialogs.reRender.haveLabel'), v: <TokenAmount locale={locale} n={balance} place="table" /> },
          { k: t('dialogs.reRender.leftLabel'), v: <TokenAmount locale={locale} n={after} place="table" /> },
        ]}
      />
    </Dialog>
  )
}
